import React, { useEffect, useCallback, useRef } from "react";
import { Handle, Position, useReactFlow, useUpdateNodeInternals } from "@xyflow/react";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import SvgIcon from "../../../../components/SvgIcon";
import NodeControl from "../components/node-control.js";
import { useNodeDataUpdate } from "../../hooks/use-node-data-update.js";

const Conversation = ({
  id,
  data,
  selected,
}) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const { updateNodeData, setEdges } = useReactFlow();
  const { handleNodeDataUpdate } = useNodeDataUpdate();

  // Use refs for uncontrolled inputs - prevents any state updates from affecting DOM during typing
  const titleInputRef = useRef(null);
  const instructionRef = useRef(null);
  const transitionRefs = useRef([]);

  // Track current values in refs to avoid state updates
  const titleValueRef = useRef(data.title);
  const instructionValueRef = useRef(data.instruction);

  // Track cursor positions to restore after re-renders
  const titleCursorRef = useRef(0);
  const instructionCursorRef = useRef(0);

  // Update refs when data changes from parent (but don't update DOM to preserve cursor)
  useEffect(() => {
    titleValueRef.current = data.title;
  }, [data.title]);

  useEffect(() => {
    instructionValueRef.current = data.instruction;
  }, [data.instruction]);

  const transition_text_onchange = useCallback(
    (index, text) => {
      const currentTransitions = data.transition || [];

      const newTransitions = [...currentTransitions];

      if (newTransitions[index]) {
        newTransitions[index] = {
          ...newTransitions[index],
          text: text,
        };
      }

      handleNodeDataUpdate({ id, data: { transition: newTransitions } });
    },
    [id, data.transition, handleNodeDataUpdate]
  );

  const transition_add = useCallback(() => {
    const currentTransitions = data.transition || [];

    const newTransitions = [...currentTransitions];

    updateNodeData(id, {
      transition: [
        ...newTransitions,
        {
          handleId: "transition-" + new Date().getTime(),
          text: "Describe the transition condition",
        },
      ],
    });
    updateNodeInternals(id);
  }, [data.transition, updateNodeData, id, updateNodeInternals]);

  const transition_del = useCallback(
    (index) => {
      const currentTransitions = data.transition || [];
      const toDelHandleId = currentTransitions[index].handleId;
      const newTransitions = currentTransitions.filter((_, i) => i !== index);
      setEdges((edges) =>
        edges.filter(
          (edge) => !(edge.sourceHandle === toDelHandleId && edge.source === id)
        )
      );
      updateNodeData(id, { transition: newTransitions });
      updateNodeInternals(id);
    },
    [data.transition, setEdges, updateNodeData, id, updateNodeInternals]
  );

  const handleBlur = useCallback(
    (key, value) => {
      const trimmedValue = value.trim();

      const normalizedValue = trimmedValue.replace(/\s+/g, " ");

      updateNodeData(id, { [key]: normalizedValue });
    },
    [id, updateNodeData]
  );

  const handleTransitionBlur = useCallback(
    (index, value) => {
      const currentTransitions = data.transition || [];
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\s+/g, " ");

      const newTransitions = [...currentTransitions];
      if (newTransitions[index]) {
        newTransitions[index] = {
          ...newTransitions[index],
          text: normalizedValue,
        };
      }
      updateNodeData(id, { transition: newTransitions });
    },
    [id, data.transition, updateNodeData]
  );

  return (
    <div
      className={cn(
        "w-[300px] group inline-flex flex-col items-start justify-start rounded-lg bg-[#FDF2F8]",
        selected && "outline-2"
      )}
    >
      <Handle className="!top-5" type="target" position={Position.Left} />
      <header className="flex w-full items-center justify-start gap-2 px-3 pb-2 pt-3">
        <span>#</span>
        <Input
          ref={titleInputRef}
          className="nodrag nowheel"
          type="text"
          defaultValue={data.title}
          placeholder="Conversation"
          onKeyDown={(evt) => {
            evt.stopPropagation();
            if (titleInputRef.current) {
              titleCursorRef.current = titleInputRef.current.selectionStart || 0;
            }
          }}
          onKeyUp={(evt) => {
            evt.stopPropagation();
            if (titleInputRef.current) {
              titleCursorRef.current = titleInputRef.current.selectionStart || 0;
            }
          }}
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("title", evt.target.value)}
        />
        <NodeControl id={id} selected={selected} />
      </header>
      <div className="flex w-full flex-col items-start justify-start gap-0.5 p-1.5">
        <Textarea
          ref={instructionRef}
          defaultValue={data.instruction}
          onKeyDown={(evt) => {
            evt.stopPropagation();
            if (instructionRef.current) {
              instructionCursorRef.current = instructionRef.current.selectionStart || 0;
            }
          }}
          onKeyUp={(evt) => {
            evt.stopPropagation();
            if (instructionRef.current) {
              instructionCursorRef.current = instructionRef.current.selectionStart || 0;
            }
          }}
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("instruction", evt.target.value)}
          className="nodrag nowheel resize-none bg-white min-h-44 max-h-80"
        />
        <div className="mt-2 flex items-center gap-3 self-stretch p-1">
          <label className="text-sm text-gray-600 flex-1">
            Prompt
          </label>
          <div
            onMouseDown={(evt) => evt.stopPropagation()}
            onClick={(evt) => {
              evt.stopPropagation();
              updateNodeData(id, { readExactly: !data.readExactly });
            }}
            className={`nodrag relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
              !data.readExactly ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                !data.readExactly ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>
        {/* Require exact answer — ON (blue) = only a listed option is accepted; an ambiguous
            partial asks which one, and an off-script answer re-reads the question instead of
            being force-matched */}
        <div className="flex items-center gap-3 self-stretch p-1">
          <label className="text-sm text-gray-600 flex-1">
            Require exact answer
          </label>
          <div
            onMouseDown={(evt) => evt.stopPropagation()}
            onClick={(evt) => {
              evt.stopPropagation();
              updateNodeData(id, { strictAnswer: data.strictAnswer !== true });
            }}
            className={`nodrag relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
              data.strictAnswer === true ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                data.strictAnswer === true ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>
        <div className="mt-1 flex flex-col items-start justify-start gap-1.5 self-stretch rounded-lg bg-white p-2">
          <div className="inline-flex h-6 w-full items-center justify-between self-stretch pb-2">
            <span className="h-5 text-sm font-normal leading-tight text-[#99a0ae]">
              Transition
            </span>
            <Button variant="link" onClick={transition_add}>
              Add
            </Button>
          </div>
          <div className="relative flex w-full flex-col gap-1">
            {data?.transition?.map((item, index) => (
              <div
                key={item.handleId}
                className="group/item relative flex items-center justify-start gap-1 self-stretch rounded-md bg-[#f5f7fa] p-2 py-3"
              >
                <span>{index + 1}</span>
                <Textarea
                  ref={(el) => {
                    if (el) transitionRefs.current[index] = el;
                  }}
                  defaultValue={item.text}
                  className="nodrag nowheel resize-none bg-white min-h-24 max-h-48"
                  onBlur={(evt) =>
                    handleTransitionBlur(index, evt.target.value)
                  }
                />
                <Button
                  variant="link"
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hidden group-hover/item:block !p-0 h-fit"
                  onClick={() => transition_del(index)}
                >
                  <SvgIcon
                    name="material-symbols-light:delete-outline"
                    className=""
                  />
                </Button>

                <Handle
                  className="!-right-4"
                  type="source"
                  position={Position.Right}
                  id={item.handleId}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Conversation, (prevProps, nextProps) => {
  // Only re-render if id or data changes, ignore selected
  return (
    prevProps.id === nextProps.id &&
    prevProps.data === nextProps.data
  );
});
